'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Spinner } from '@common/components';
import { useClipboard, useToast } from '@common/hooks';
import { SvgAlignBack } from '@common/icon';
import { cn, isDev } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { UnauthQueryOptions } from '@/api';
import { useInviteEventPeriodValue } from '@/app/donation/event/market/invite/_hooks/useInviteEventPeriodValue';
import { INVITE_COMMON_CONFIG } from '@/app/donation/invite/constants';
import { BREAKPOINTS, DONATION_ROUTES, ENV } from '@/constants';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useKakaoShare } from '@/hooks/useKakaoShare';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
interface KakaoWindow extends Window {
  Kakao?: {
    isInitialized?: () => boolean;
    init?: (clientId: string) => void;
    Share?: {
      sendScrap?: (options: {
        requestUrl: string;
      }) => void;
      sendDefault?: (options: {
        objectType: string;
        content: {
          title: string;
          description: string;
          imageUrl: string;
          link: {
            mobileWebUrl: string;
            webUrl: string;
          };
        };
        buttons?: Array<{
          title: string;
          link: {
            mobileWebUrl: string;
            webUrl: string;
          };
        }>;
      }) => void;
    };
  };
}
export const ShareBottomNavPortal = () => {
  const $ = _c(48);
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const [webviewType] = useDeviceInfoStore(useShallow(_temp));
  const {
    isWelloApp
  } = useCheckWelloApp();
  const isKbApp = webviewType === WEBVIEW_TYPE.KB_PAY;
  const {
    isInitialized
  } = useKakaoShare();
  const memberCode = useAuthStore(_temp2);
  let t0;
  if ($[0] !== memberCode) {
    t0 = UnauthQueryOptions["get:/wello/v2/allows/home-donation-event-history/member-name"]({
      query: {
        memberCode: memberCode as string
      }
    });
    $[0] = memberCode;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !!memberCode;
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      ...t0,
      enabled: t1,
      select: _temp3
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: name
  } = useQuery(t2);
  let url;
  if ($[5] !== memberCode) {
    let mainDomain = ENV.NEXT_PUBLIC_DOMAIN;
    if (!isDev) {
      mainDomain = mainDomain.replace("https://", "https://www.");
    }
    url = new URL(DONATION_ROUTES.MARKET.pathname, mainDomain);
    if (memberCode) {
      url.searchParams.set("inviterCode", memberCode);
    }
    $[5] = memberCode;
    $[6] = url;
  } else {
    url = $[6];
  }
  const shareLinkUrl = url.href;
  const copy = useClipboard();
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      type: MESSAGE_KEYWORD.CHECK_KAKAO_INSTALLED
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    request: checkKakaoInstallRequest,
    response: checkKakaoInstallResponse
  } = useHandleApp(t3);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      type: MESSAGE_KEYWORD.SHARE_CONTENT
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    request: requestShare,
    isPending
  } = useHandleApp(t4);
  const {
    toast
  } = useToast();
  let t5;
  let t6;
  if ($[9] !== checkKakaoInstallRequest) {
    t5 = () => {
      checkKakaoInstallRequest();
    };
    t6 = [checkKakaoInstallRequest];
    $[9] = checkKakaoInstallRequest;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  const shareTitle = name ? `${name}님이 최대 34만원의 12월 혜택을 선물했어요!` : "\uCD5C\uB300 34\uB9CC\uC6D0\uC758 12\uC6D4 \uD61C\uD0DD\uC744 \uC120\uBB3C\uD588\uC5B4\uC694!";
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      ssrFallbackValue: "https://cdn.welfarehello.com/static/event/home-love/market/og-image-1.png"
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const shareThumbnail = useInviteEventPeriodValue("https://cdn.welfarehello.com/static/event/home-love/market/og-image-1.png", "https://cdn.welfarehello.com/static/event/home-love/market/og-image-2.png", t7);
  let t8;
  if ($[13] !== checkKakaoInstallResponse?.isSuccess || $[14] !== isPending || $[15] !== requestShare || $[16] !== shareLinkUrl || $[17] !== shareThumbnail || $[18] !== shareTitle || $[19] !== toast) {
    t8 = async () => {
      if (isPending) {
        return;
      }
      if (checkKakaoInstallResponse?.isSuccess && shareLinkUrl) {
        requestShare({
          shareType: "kakao",
          url: shareLinkUrl,
          thumbnail: shareThumbnail,
          subject: shareTitle,
          description: "\uCE5C\uAD6C \uCD08\uB300\uC7A5\uC73C\uB85C \uAC00\uC785\uD558\uBA74\n\uAC00\uC785 \uC989\uC2DC 1,000\uC6D0\uC744 \uBC1B\uC744 \uC218 \uC788\uC5B4\uC694."
        });
      } else {
        toast({
          message: "\uCE74\uCE74\uC624\uD1A1\uC774 \uC124\uCE58\uB418\uC5B4 \uC788\uC9C0 \uC54A\uC544\uC694",
          type: "fail"
        });
      }
    };
    $[13] = checkKakaoInstallResponse?.isSuccess;
    $[14] = isPending;
    $[15] = requestShare;
    $[16] = shareLinkUrl;
    $[17] = shareThumbnail;
    $[18] = shareTitle;
    $[19] = toast;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleKakaoClickInApp = t8;
  let t9;
  if ($[21] !== shareLinkUrl || $[22] !== shareThumbnail || $[23] !== shareTitle) {
    t9 = async () => {
      if (!shareLinkUrl) {
        return;
      }
      const kakao = (window as KakaoWindow).Kakao;
      kakao?.Share?.sendDefault?.({
        objectType: "feed",
        content: {
          title: shareTitle,
          description: "\uCE5C\uAD6C \uCD08\uB300\uC7A5\uC73C\uB85C \uAC00\uC785\uD558\uBA74\n\uAC00\uC785 \uC989\uC2DC 1,000\uC6D0\uC744 \uBC1B\uC744 \uC218 \uC788\uC5B4\uC694.",
          imageUrl: shareThumbnail,
          link: {
            mobileWebUrl: shareLinkUrl,
            webUrl: shareLinkUrl
          }
        },
        buttons: [{
          title: "\uC9C0\uAE08 \uBC14\uB85C \uD655\uC778\uD558\uAE30",
          link: {
            mobileWebUrl: shareLinkUrl,
            webUrl: shareLinkUrl
          }
        }]
      });
    };
    $[21] = shareLinkUrl;
    $[22] = shareThumbnail;
    $[23] = shareTitle;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleKakaoClickInWeb = t9;
  const isKakaoShareButtonShow = !isKbApp;
  let isDeadlinePassed;
  let t10;
  let t11;
  let t12;
  if ($[25] !== handleKakaoClickInApp || $[26] !== handleKakaoClickInWeb || $[27] !== isInitialized || $[28] !== isKakaoShareButtonShow || $[29] !== isWelloApp) {
    t12 = Symbol.for("react.early_return_sentinel");
    bb0: {
      isDeadlinePassed = dayjs().isAfter(dayjs(INVITE_COMMON_CONFIG.event.endDate));
      if (isDeadlinePassed) {
        t12 = null;
        break bb0;
      }
      if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = cn("flex w-full justify-center gap-8 bg-Gray-white py-12", "mobile-tablet:sticky mobile-tablet:bottom-0 mobile-tablet:mt-auto mobile-tablet:flex-col mobile-tablet:px-20 mobile-tablet:shadow-lg");
        $[34] = t10;
      } else {
        t10 = $[34];
      }
      t11 = isKakaoShareButtonShow ? <button type="button" onClick={isWelloApp ? handleKakaoClickInApp : handleKakaoClickInWeb} className={cn("relative h-48 w-240 gap-8 rounded-xsmall bg-[#FFDE32] flex-center", "mobile-tablet:w-full", isDeadlinePassed && "cursor-not-allowed opacity-50")} disabled={!isInitialized || isDeadlinePassed}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.677 20.813C5.498 20.707 5.527 20.692 6.57 17.113C3.827 15.766 2 13.366 2 10.628C2 6.416 6.328 3 11.662 3H11.739C17.099 3 21.446 6.412 21.446 10.623C21.446 14.456 17.846 17.629 13.146 18.165C12.1 18.29 11.04 18.28 10 18.141C6.766 20.408 6.071 20.841 5.8 20.841C5.76 20.84 5.71 20.83 5.677 20.813Z" fill="#1A1A1A" /></svg><p className="font-700 text-Gray-black typo-body4">카카오로 초대하기</p>{!isInitialized ? <div className="absolute left-0 top-0 size-full bg-Gray-white/50 flex-center"><Spinner size={16} /></div> : <></>}</button> : null;
    }
    $[25] = handleKakaoClickInApp;
    $[26] = handleKakaoClickInWeb;
    $[27] = isInitialized;
    $[28] = isKakaoShareButtonShow;
    $[29] = isWelloApp;
    $[30] = isDeadlinePassed;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
  } else {
    isDeadlinePassed = $[30];
    t10 = $[31];
    t11 = $[32];
    t12 = $[33];
  }
  if (t12 !== Symbol.for("react.early_return_sentinel")) {
    return t12;
  }
  const t13 = isDesktopViewport ? "sub-line" : "sub-text";
  let t14;
  if ($[35] !== copy || $[36] !== shareLinkUrl) {
    t14 = () => {
      ;
      try {
        if (shareLinkUrl) {
          return copy({
            value: shareLinkUrl
          });
        }
      } catch (t15) {
        const e = t15;
        console.error(e);
      }
    };
    $[35] = copy;
    $[36] = shareLinkUrl;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  let t16;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn("w-240", "mobile-tablet:w-full");
    t16 = <SvgAlignBack className="size-14 text-Gray-600" />;
    $[38] = t15;
    $[39] = t16;
  } else {
    t15 = $[38];
    t16 = $[39];
  }
  let t17;
  if ($[40] !== isDeadlinePassed || $[41] !== t13 || $[42] !== t14) {
    t17 = <Button theme={t13} dimensions="H48" disabled={isDeadlinePassed} onClick={t14} className={t15}>{t16}링크 복사하기</Button>;
    $[40] = isDeadlinePassed;
    $[41] = t13;
    $[42] = t14;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t10 || $[45] !== t11 || $[46] !== t17) {
    t18 = <footer className={t10}>{t11}{t17}</footer>;
    $[44] = t10;
    $[45] = t11;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  return t18;
};
function _temp(state) {
  return [state.webviewType];
}
function _temp2(state_0) {
  return state_0.myInfo?.memberCode;
}
function _temp3(t0) {
  const {
    context
  } = t0;
  return context?.name;
}